import { MegaphoneIcon } from "@phosphor-icons/react/dist/csr/Megaphone";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEventListener } from "usehooks-ts";
import { useSidebar } from "@volca/ui/components/ui/sidebar";
import { CHANGELOG_PATH } from "@/changelog/routes";
import { useBusinessProducts } from "@/core/business/use-business-products";
import { HELP_PATH, isHelpPath } from "@/help/routes";
import { getAvailableHelpTopics } from "@/help/topics";
import { navPanelWidthCookie, readNavPanelWidth } from "./nav-panel-width";
import { useUnseenNewItems } from "./new-badge-seen";
import {
  buildHelpRail,
  buildProductRail,
  buildSettingsRail,
  previewPin,
  type RailCluster,
  type RailEntry,
} from "./rail-model";
import type { NavItem } from "./use-nav-items";
import type { SettingsNav } from "./use-settings-nav";

const PREVIEW_OPEN_MS = 200;
const PREVIEW_CLOSE_MS = 150;

interface Preview {
  entry: RailEntry;
  pin: "top" | "bottom";
}

interface NavColumnContextValue {
  productGroups: RailCluster[];
  utility: RailEntry[];
  active: RailEntry | null;
  unseenHrefs: Set<string>;
  isLoading: boolean;
  hidden: boolean;
  snap: boolean;
  show: () => void;
  hide: () => void;
  select: (entry: RailEntry) => void;
  width: {
    value: number;
    set: (width: number) => void;
    commit: () => void;
  };
  preview: {
    current: Preview | null;
    show: (entry: RailEntry) => void;
    hideSoon: () => void;
    hold: () => void;
    close: () => void;
  };
}

const NavColumnContext = createContext<NavColumnContextValue | null>(null);

export const useNavColumn = () => {
  const context = useContext(NavColumnContext);
  if (!context) {
    throw new Error("useNavColumn must be used within NavColumnProvider.");
  }
  return context;
};

export const NavColumnProvider = ({
  items,
  settings,
  isLimitedAccess,
  isLoading,
  children,
}: {
  items: NavItem[];
  settings: SettingsNav | null;
  isLimitedAccess: boolean;
  isLoading: boolean;
  children: ReactNode;
}) => {
  const { pathname, key: locationKey } = useLocation();
  const navigate = useNavigate();
  const { open, setOpen } = useSidebar();
  const { settings: productSettings } = useBusinessProducts();
  const unseenHrefs = useUnseenNewItems(items);
  const [width, setWidthState] = useState(() =>
    readNavPanelWidth(document.cookie),
  );
  const widthRef = useRef(width);
  widthRef.current = width;
  const hidden = !open;
  const [snap, setSnap] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);
  const showTimer = useRef<number | null>(null);
  const hideTimer = useRef<number | null>(null);
  const closePreview = useCallback(() => {
    if (showTimer.current != null) window.clearTimeout(showTimer.current);
    if (hideTimer.current != null) window.clearTimeout(hideTimer.current);
    showTimer.current = null;
    hideTimer.current = null;
    setPreview(null);
  }, []);
  const productGroups = buildProductRail(items);
  const help =
    isLimitedAccess || isLoading
      ? null
      : buildHelpRail(
          getAvailableHelpTopics(productSettings),
          pathname.slice(HELP_PATH.length + 1).replace(/\/+$/, ""),
          isHelpPath(pathname),
        );
  const utility: RailEntry[] = [buildSettingsRail(settings), help].filter(
    (entry) => entry !== null,
  );
  if (!isLoading) {
    utility.push({
      id: CHANGELOG_PATH,
      label: "What’s new",
      icon: MegaphoneIcon,
      to: CHANGELOG_PATH,
      isActive: pathname === CHANGELOG_PATH,
      sections: [],
      sourceHrefs: [],
    });
  }
  const active =
    [...productGroups.flatMap((group) => group.entries), ...utility].find(
      (entry) => entry.sections.length > 0 && entry.isActive,
    ) ?? null;
  const activeId = active?.id ?? null;
  const activeRef = useRef(active);
  const hiddenRef = useRef(hidden);
  activeRef.current = active;
  hiddenRef.current = hidden;
  const previousActiveId = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (previousActiveId.current === activeId) return;
    previousActiveId.current = activeId;
    setOpen(true);
    setSnap(true);
  }, [activeId, setOpen]);

  useEffect(() => closePreview, [locationKey, activeId, hidden, closePreview]);

  useEventListener("keydown", (event) => {
    if (event.key === "Escape") closePreview();
  });

  useEffect(() => {
    if (!snap) return;
    const frame = requestAnimationFrame(() => setSnap(false));
    return () => cancelAnimationFrame(frame);
  }, [snap]);

  const show = () => {
    closePreview();
    setOpen(true);
  };
  const hide = () => {
    closePreview();
    setOpen(false);
  };
  const select = (entry: RailEntry) => {
    closePreview();
    entry.onNavigate?.();
    if (hiddenRef.current) setOpen(true);
    if (entry.isActive) return;
    const to = entry.sections[0]?.to;
    if (to) navigate(to);
  };
  const setWidth = (next: number) => {
    widthRef.current = next;
    setWidthState(next);
  };
  const commitWidth = () => {
    document.cookie = navPanelWidthCookie(widthRef.current);
  };
  const showPreview = (entry: RailEntry) => {
    if (entry.sections.length === 0) return;
    if (activeRef.current && !hiddenRef.current) return;
    if (hideTimer.current != null) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
    const next = { entry, pin: previewPin(entry) };
    if (preview) {
      if (preview.entry.id !== entry.id) setPreview(next);
      return;
    }
    if (showTimer.current != null) return;
    showTimer.current = window.setTimeout(() => {
      showTimer.current = null;
      setPreview(next);
    }, PREVIEW_OPEN_MS);
  };
  const hidePreviewSoon = () => {
    if (showTimer.current != null) {
      window.clearTimeout(showTimer.current);
      showTimer.current = null;
    }
    if (hideTimer.current != null) window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => {
      hideTimer.current = null;
      setPreview(null);
    }, PREVIEW_CLOSE_MS);
  };
  const holdPreview = () => {
    if (hideTimer.current != null) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };

  return (
    <NavColumnContext.Provider
      value={{
        productGroups,
        utility,
        active,
        unseenHrefs,
        isLoading,
        hidden,
        snap,
        show,
        hide,
        select,
        width: { value: width, set: setWidth, commit: commitWidth },
        preview: {
          current: preview,
          show: showPreview,
          hideSoon: hidePreviewSoon,
          hold: holdPreview,
          close: closePreview,
        },
      }}
    >
      {children}
    </NavColumnContext.Provider>
  );
};
